import React, { useEffect, useRef, useState } from "react";
import {
  api,
  fmtCNPJ,
  fmtCurrency,
  fmtDateTime,
  formatApiErrorDetail,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Download,
  MagnifyingGlass,
  ShieldCheck,
  X,
  Archive,
  CalendarBlank,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import DanfePreview from "@/components/DanfePreview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { CloudDownload } from "lucide-react";
import { Label } from "@/components/ui/label";
import { API_BASE, getToken } from "@/lib/api";
import { confirmToast } from "@/utils/toastConfirm";

const Documents = () => {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    q: "",
    modelo: "",
    tipo: "",
    manifestado: "",
  });
  const [openDetail, setOpenDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [openBulk, setOpenBulk] = useState(false);
  const [bulkRange, setBulkRange] = useState({
    date_from: "",
    date_to: "",
    cnpj: "",
    modelo: "",
  });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [pendingDownloads, setPendingDownloads] = useState(new Set());
  const pendingDownloadsRef = useRef(new Set());
  const tableScrollRef = useRef(null);
  const filterRef = useRef(filter);
  const listRequestRef = useRef(0);
  const openDocumentRef = useRef(null);

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    openDocumentRef.current = openDetail ? detail : null;
  }, [openDetail, detail]);

  const startDownload = (key) => {
    if (pendingDownloadsRef.current.has(key)) return false;
    pendingDownloadsRef.current.add(key);
    setPendingDownloads(new Set(pendingDownloadsRef.current));
    return true;
  };

  const finishDownload = (key) => {
    pendingDownloadsRef.current.delete(key);
    setPendingDownloads(new Set(pendingDownloadsRef.current));
  };

  const scrollDocumentsTable = (target) => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.scrollTo({
      left: target === "end" ? el.scrollWidth : 0,
      behavior: "smooth",
    });
  };

  const load = async ({ background = false } = {}) => {
    const requestId = ++listRequestRef.current;
    if (!background) setLoading(true);
    try {
      const currentFilter = filterRef.current;
      const params = { limit: 100 };
      if (currentFilter.q) params.q = currentFilter.q;
      if (currentFilter.modelo) params.modelo = currentFilter.modelo;
      if (currentFilter.tipo) params.tipo = currentFilter.tipo;
      if (currentFilter.manifestado !== "")
        params.manifestado = currentFilter.manifestado === "true";
      if (currentFilter.tenant_id) params.tenant_id = currentFilter.tenant_id;

      const r = await api.get("/nfe", { params });
      if (requestId === listRequestRef.current) {
        setItems(r.data.items);
        setTotal(r.data.total);
      }
    } catch {
      if (requestId === listRequestRef.current)
        toast.error("Erro ao carregar documentos");
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [filter.modelo, filter.tipo, filter.manifestado, filter.tenant_id]);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const r = await api.get("/tenants");
        setTenants(r.data.items || r.data);
      } catch (e) {
        console.error("Erro ao carregar as empresas para o filtro", e);
      }
    };
    fetchTenants();
  }, []);

  const openDoc = async (chave, { refresh = false } = {}) => {
    try {
      const r = await api.get(`/nfe/${chave}`);
      if (refresh && openDocumentRef.current?.document.chave !== chave) return;
      setDetail(r.data);
      if (!refresh) setOpenDetail(true);
    } catch {
      toast.error("Erro ao carregar detalhes");
    }
  };

  const downloadXml = async (chave) => {
    const key = `xml-${chave}`;
    if (!startDownload(key)) return;
    try {
      const r = await api.get(`/nfe/${chave}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(r.data);
      const a = window.document.createElement("a");
      try {
        a.href = url;
        a.download = `${chave}.xml`;
        window.document.body.appendChild(a);
        a.click();
      } finally {
        a.remove();
        // Give the browser time to consume the download before releasing it.
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
      toast.success("Download iniciado");
    } catch (e) {
      let errorDetail = e?.response?.data?.detail;
      if (e?.response?.data instanceof Blob) {
        try {
          errorDetail = JSON.parse(await e.response.data.text()).detail;
        } catch {
          // A proxy or network failure may not return a JSON error body.
        }
      }
      toast.error(
        errorDetail ? formatApiErrorDetail(errorDetail) : "Erro ao baixar XML",
      );
    } finally {
      finishDownload(key);
    }
  };

  const downloadBulk = async () => {
    setBulkLoading(true);
    try {
      const params = new URLSearchParams();
      if (bulkRange.date_from) params.append("date_from", bulkRange.date_from);
      if (bulkRange.date_to) params.append("date_to", bulkRange.date_to);
      if (bulkRange.cnpj) params.append("cnpj", bulkRange.cnpj);
      if (bulkRange.modelo) params.append("modelo", bulkRange.modelo);

      const res = await fetch(
        `${API_BASE}/nfe-bulk/download?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          err.detail || "Nenhum documento encontrado para o período",
        );
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download =
        res.headers
          .get("content-disposition")
          ?.match(/filename="(.+?)"/)?.[1] || "nfe.zip";
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download iniciado");
      setOpenBulk(false);
    } catch (e) {
      toast.error(e.message || "Erro ao baixar");
    } finally {
      setBulkLoading(false);
    }
  };

  const manifestar = (chave) => {
    confirmToast("Enviar manifestação 210210 (Ciência da Operação)?", async () => {
      try {
        const r = await api.post(`/nfe/${chave}/manifestar`);
        
        if (r.data.ok) {
          toast.success(`Manifestação enviada — cStat ${r.data.cstat}`);
        } else {
          toast.error(`Rejeição da SEFAZ: ${r.data.motivo} (cStat ${r.data.cstat})`);
        }
        
        load();
        if (detail && detail.document.chave === chave) openDoc(chave);
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) || "Erro ao manifestar"
        );
      }
    }, "Manifestar", false);
  };

  const baixarXmlSefaz = async (chave) => {
    const key = `sefaz-${chave}`;
    if (!startDownload(key)) return;
    try {
      toast.info("Buscando XML completo na SEFAZ, aguarde...");

      const r = await api.post(`/nfe/${chave}/download-sefaz`);

      toast.success(r.data.message || "XML completo atualizado com sucesso!");
      await load({ background: true });

      if (openDocumentRef.current?.document.chave === chave)
        openDoc(chave, { refresh: true });
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Erro ao baixar XML da SEFAZ",
      );
    } finally {
      finishDownload(key);
    }
  };

  return (
    <div data-testid="page-documents">
      <PageHeader
        title="Documentos NF-e / NFC-e"
        subtitle="Documentos capturados automaticamente e organizados com segurança, sem duplicidade."
        actions={
          <Button
            onClick={() => setOpenBulk(true)}
            variant="outline"
            data-testid="bulk-download-btn"
            className="rounded-sm"
          >
            <Archive size={16} /> Baixar período (ZIP)
          </Button>
        }
      />

      <div className="px-4 md:px-8 py-6">
        <div className="bg-white border border-border rounded-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              placeholder="Buscar chave, CNPJ ou nome do emitente..."
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="filter-q"
              className="pl-8 font-mono text-xs"
            />
          </div>

          <Select
            value={filter.tenant_id || "all"}
            onValueChange={(v) =>
              setFilter({ ...filter, tenant_id: v === "all" ? "" : v })
            }
          >
            <SelectTrigger data-testid="filter-tenant">
              <SelectValue placeholder="Sua Empresa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as empresas</SelectItem>
              {tenants.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.razao_social || fmtCNPJ(t.cnpj)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filter.modelo}
            onValueChange={(v) =>
              setFilter({ ...filter, modelo: v === "all" ? "" : v })
            }
          >
            <SelectTrigger data-testid="filter-modelo">
              <SelectValue placeholder="Modelo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos modelos</SelectItem>
              <SelectItem value="55">55 — NF-e</SelectItem>
              <SelectItem value="65">65 — NFC-e</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filter.tipo}
            onValueChange={(v) =>
              setFilter({ ...filter, tipo: v === "all" ? "" : v })
            }
          >
            <SelectTrigger data-testid="filter-tipo">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos tipos</SelectItem>
              <SelectItem value="entrada">Entrada</SelectItem>
              <SelectItem value="saida">Saída</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filter.manifestado}
            onValueChange={(v) =>
              setFilter({ ...filter, manifestado: v === "all" ? "" : v })
            }
          >
            <SelectTrigger data-testid="filter-manifestado">
              <SelectValue placeholder="Manifestação" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="true">Manifestada</SelectItem>
              <SelectItem value="false">Pendente</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-border rounded-sm">
          <div className="hidden md:flex items-center justify-end gap-1 border-b border-border bg-muted/20 px-2 py-1">
            <button
              type="button"
              title="Ir para o início da tabela"
              aria-label="Ir para o início da tabela"
              onClick={() => scrollDocumentsTable("start")}
              className="h-7 w-7 inline-flex items-center justify-center rounded-sm border border-border bg-white hover:bg-muted"
            >
              <CaretLeft size={14} />
            </button>
            <button
              type="button"
              title="Ir para as ações da tabela"
              aria-label="Ir para as ações da tabela"
              onClick={() => scrollDocumentsTable("end")}
              className="h-7 w-7 inline-flex items-center justify-center rounded-sm border border-border bg-white hover:bg-muted"
            >
              <CaretRight size={14} />
            </button>
          </div>
          <div
            ref={tableScrollRef}
            className="overflow-x-auto table-scrollbar pb-2"
          >
          <table
            className="data-table min-w-[1220px]"
            data-testid="documents-table"
          >
            <thead>
              <tr>
                <th className="w-[315px] whitespace-nowrap">Chave NF-e</th>
                <th className="w-[82px] whitespace-nowrap">Modelo</th>
                <th className="w-[92px] whitespace-nowrap">Tipo</th>
                <th className="w-[250px] whitespace-nowrap">Emitente</th>
                <th className="w-[230px] whitespace-nowrap">Sua Empresa</th>
                <th className="w-[110px] whitespace-nowrap text-right">Valor</th>
                <th className="w-[130px] whitespace-nowrap">Emissão</th>
                <th className="w-[130px] whitespace-nowrap">Manifestação</th>
                <th className="w-[110px] whitespace-nowrap text-right sticky right-0 z-20 bg-muted/95 shadow-[-12px_0_18px_-18px_rgba(15,23,42,0.8)]">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={9}
                    className="text-center py-8 text-muted-foreground"
                  >
                    Carregando...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="text-center py-12 text-muted-foreground"
                  >
                    Nenhum documento encontrado.
                  </td>
                </tr>
              )}
              {items.map((d) => (
                <tr
                  key={d.chave}
                  data-testid={`doc-row-${d.chave}`}
                  onClick={() => openDoc(d.chave)}
                  className="cursor-pointer group"
                >
                  <td className="font-mono text-[11px] whitespace-nowrap">
                    {d.chave}
                  </td>
                  <td className="whitespace-nowrap">
                    <StatusBadge
                      variant={d.modelo === "55" ? "info" : "neutral"}
                    >
                      {d.modelo}
                    </StatusBadge>
                  </td>
                  <td className="whitespace-nowrap">
                    {d.tipo_operacao ? (
                      <StatusBadge
                        variant={
                          d.tipo_operacao === "entrada" ? "success" : "neutral"
                        }
                      >
                        {d.tipo_operacao === "entrada" ? "Entrada" : "Saída"}
                      </StatusBadge>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td>
                    <div
                      className="text-sm max-w-[230px] truncate"
                      title={d.nome_emitente || "Resumo de Evento"}
                    >
                      {d.nome_emitente || (
                        <span className="text-amber-600 text-xs font-semibold">
                          Resumo de Evento
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono max-w-[230px] truncate">
                      {d.cnpj_emitente
                        ? fmtCNPJ(d.cnpj_emitente)
                        : "Verifique os detalhes na SEFAZ"}
                    </div>
                  </td>
                  <td>
                    <div
                      className="text-sm max-w-[210px] truncate"
                      title={
                        tenants?.find((t) => t.id === d.tenant_id)
                          ?.razao_social || "Sua Empresa"
                      }
                    >
                      {tenants?.find((t) => t.id === d.tenant_id)
                        ?.razao_social || "Sua Empresa"}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                      {d.cnpj_destinatario ? fmtCNPJ(d.cnpj_destinatario) : ""}
                    </div>
                  </td>
                  <td className="text-right font-mono whitespace-nowrap">
                    {fmtCurrency(d.valor_total)}
                  </td>
                  <td className="text-xs whitespace-nowrap">
                    {fmtDateTime(d.dh_emi)}
                  </td>
                  <td className="whitespace-nowrap">
                    {d.manifestado ? (
                      <StatusBadge variant="success">Manifestada</StatusBadge>
                    ) : d.modelo === "55" && d.tipo_operacao === "entrada" ? (
                      d.manifestacao_status === "error" ? (
                        <div title={d.manifestacao_motivo || "Erro na solicitação"}>
                          <StatusBadge variant="error">Erro</StatusBadge>
                        </div>
                      ) : (
                        <StatusBadge variant="warning">Pendente</StatusBadge>
                      )
                    ) : (
                      <StatusBadge variant="neutral">N/A</StatusBadge>
                    )}
                  </td>
                  <td className="sticky right-0 z-10 bg-white group-hover:bg-muted/30 shadow-[-12px_0_18px_-18px_rgba(15,23,42,0.8)]">
                    <div
                      className="flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => downloadXml(d.chave)}
                        disabled={pendingDownloads.has(`xml-${d.chave}`)}
                        aria-busy={pendingDownloads.has(`xml-${d.chave}`)}
                        title="Baixar XML atual (Resumo ou Completo)"
                        data-testid={`download-${d.chave}`}
                        className="p-1.5 rounded-sm hover:bg-muted disabled:opacity-50 disabled:cursor-wait"
                      >
                        <Download size={16} />
                      </button>

                      {d.modelo === "55" &&
                        d.tipo_operacao === "entrada" &&
                        !d.manifestado && (
                          <button
                            onClick={() => manifestar(d.chave)}
                            title="Manifestar 210210 (Ciência da Operação)"
                            data-testid={`manifestar-${d.chave}`}
                            className="p-1.5 rounded-sm hover:bg-muted text-amber-600"
                          >
                            <ShieldCheck size={16} />
                          </button>
                        )}

                      {d.modelo === "55" &&
                        d.tipo_operacao === "entrada" &&
                        d.manifestado &&
                        !d.documento_completo && (
                          <button
                            type="button"
                            onClick={() => baixarXmlSefaz(d.chave)}
                            disabled={pendingDownloads.has(`sefaz-${d.chave}`)}
                            aria-busy={pendingDownloads.has(`sefaz-${d.chave}`)}
                            title="Buscar XML Completo na SEFAZ"
                            data-testid={`buscar-sefaz-${d.chave}`}
                            className="p-1.5 rounded-sm hover:bg-muted text-blue-600 disabled:opacity-50 disabled:cursor-wait"
                          >
                            <CloudDownload size={16} />
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          {total} documento(s)
        </div>
      </div>

      <Sheet open={openDetail} onOpenChange={setOpenDetail}>
        <SheetContent
          className="w-[95vw] sm:w-full sm:max-w-3xl overflow-y-auto"
          data-testid="doc-detail"
        >
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-mono text-sm break-all">
                  {detail.document.chave}
                </SheetTitle>
                <SheetDescription>
                  {detail.document.nome_emitente}
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="danfe" className="mt-6">
                <TabsList data-testid="detail-tabs">
                  <TabsTrigger value="danfe" data-testid="tab-danfe">
                    DANFE
                  </TabsTrigger>
                  <TabsTrigger value="dados" data-testid="tab-dados">
                    Dados
                  </TabsTrigger>
                  <TabsTrigger value="eventos" data-testid="tab-eventos">
                    Eventos · {detail.eventos.length}
                  </TabsTrigger>
                  <TabsTrigger value="xml" data-testid="tab-xml">
                    XML
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="danfe" className="mt-4">
                  <DanfePreview xml={detail.xml} document={detail.document} />
                </TabsContent>

                <TabsContent value="dados" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Info label="Modelo" value={detail.document.modelo} mono />
                    <Info
                      label="Tipo"
                      value={
                        detail.document.tipo_operacao
                          ? detail.document.tipo_operacao === "entrada"
                            ? "Entrada"
                            : "Saída"
                          : "—"
                      }
                    />
                    <Info
                      label="CNPJ Emitente"
                      value={fmtCNPJ(detail.document.cnpj_emitente)}
                      mono
                    />
                    <Info
                      label="CNPJ da Sua Empresa"
                      value={fmtCNPJ(detail.document.cnpj_destinatario)}
                      mono
                    />
                    <Info
                      label="Valor Total"
                      value={fmtCurrency(detail.document.valor_total)}
                    />
                    <Info label="NSU" value={detail.document.nsu} mono />
                    <Info label="Schema" value={detail.document.schema} mono />
                    <Info
                      label="Emissão"
                      value={fmtDateTime(detail.document.dh_emi)}
                    />
                    <Info
                      label="Capturado em"
                      value={fmtDateTime(detail.document.created_at)}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="eventos" className="mt-4">
                  {detail.eventos.length === 0 && (
                    <div className="text-xs text-muted-foreground">
                      Nenhum evento vinculado a esta NF-e.
                    </div>
                  )}
                  {detail.eventos.map((e) => (
                    <div
                      key={e.id}
                      className="border border-border rounded-sm p-3 mb-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-xs">
                          {e.tipo_evento} — {e.descricao_evento}
                        </div>
                        <StatusBadge
                          variant={
                            e.status === "success"
                              ? "success"
                              : e.status === "error"
                                ? "error"
                                : "warning"
                          }
                        >
                          {e.status === "success"
                            ? "Processado com Sucesso"
                            : e.status === "error"
                              ? "Erro na Solicitação"
                              : "Aguardando Retorno"}
                        </StatusBadge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {e.motivo} {e.cstat ? `· cStat ${e.cstat}` : ""}
                      </div>
                      {e.n_protocolo && (
                        <div className="text-xs font-mono mt-1">
                          Protocolo: {e.n_protocolo}
                        </div>
                      )}
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="xml" className="mt-4">
                  <pre className="bg-muted/40 border border-border rounded-sm p-3 text-[11px] font-mono overflow-x-auto max-h-[60vh] scrollbar-thin">
                    {detail.xml || "—"}
                  </pre>
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={openBulk} onOpenChange={setOpenBulk}>
        <DialogContent className="w-[95vw] sm:w-full" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle>Baixar XMLs por período</DialogTitle>
            <DialogDescription>
              Gere um ZIP com todos os XMLs filtrados — organizado em pastas{" "}
              <span className="font-mono">{`{cnpj}/{ano}/{mes}/`}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="from">
                <CalendarBlank size={12} className="inline mr-1" /> Data inicial
              </Label>
              <Input
                id="from"
                type="date"
                value={bulkRange.date_from}
                onChange={(e) =>
                  setBulkRange({ ...bulkRange, date_from: e.target.value })
                }
                className="mt-1.5"
                data-testid="bulk-from"
              />
            </div>
            <div>
              <Label htmlFor="to">
                <CalendarBlank size={12} className="inline mr-1" /> Data final
              </Label>
              <Input
                id="to"
                type="date"
                value={bulkRange.date_to}
                onChange={(e) =>
                  setBulkRange({ ...bulkRange, date_to: e.target.value })
                }
                className="mt-1.5"
                data-testid="bulk-to"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="cnpj-bulk">CNPJ (opcional)</Label>
              <Input
                id="cnpj-bulk"
                value={bulkRange.cnpj}
                onChange={(e) =>
                  setBulkRange({ ...bulkRange, cnpj: e.target.value })
                }
                placeholder="Apenas dígitos — em branco = todos do grupo"
                className="mt-1.5 font-mono"
                data-testid="bulk-cnpj"
              />
            </div>
            <div>
              <Label>Modelo (opcional)</Label>
              <Select
                value={bulkRange.modelo || "all"}
                onValueChange={(v) =>
                  setBulkRange({ ...bulkRange, modelo: v === "all" ? "" : v })
                }
              >
                <SelectTrigger className="mt-1.5" data-testid="bulk-modelo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="55">55 — NF-e</SelectItem>
                  <SelectItem value="65">65 — NFC-e</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenBulk(false)}>
              Cancelar
            </Button>
            <Button
              onClick={downloadBulk}
              disabled={bulkLoading}
              data-testid="bulk-submit"
            >
              {bulkLoading ? "Gerando..." : "Baixar ZIP"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Info = ({ label, value, mono }) => (
  <div>
    <div className="label-overline">{label}</div>
    <div className={"mt-0.5 text-sm " + (mono ? "font-mono" : "")}>
      {value || "—"}
    </div>
  </div>
);

export default Documents;
